import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Receipt, TrendingDown, BarChart3, ArrowUpRight } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { MonthRangePicker } from '@/components/MonthRangePicker';
import { format, subMonths, startOfMonth, endOfMonth, eachMonthOfInterval } from 'date-fns';
import { businessToday } from '@/lib/format';

const COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))'];

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);
};

const getDateFromTimestamp = (timestamp: string): string => timestamp.substring(0, 10);

export default function ExpensesAnalytics() {
    const [monthRange, setMonthRange] = useState({
        from: startOfMonth(subMonths(businessToday(), 11)),
        to: endOfMonth(businessToday()),
    });

    const fromDate = format(monthRange.from, 'yyyy-MM-dd');
    const toDate = format(monthRange.to, 'yyyy-MM-dd');

    const { data: summaryData, isLoading: summaryLoading } = useQuery({
        queryKey: ['expenses-analytics-summary', fromDate, toDate],
        queryFn: async () => {
            const { data: expenses, error } = await supabase
                .from('expenses')
                .select('id, type, description, transactions(amount, transaction_at)');
            if (error) throw error;

            const filtered = (expenses || []).filter(e => {
                const trans = e.transactions as { amount: number; transaction_at: string } | null;
                if (!trans?.transaction_at) return false;
                const transDate = getDateFromTimestamp(trans.transaction_at);
                return transDate >= fromDate && transDate <= toDate;
            });

            const totalExpenses = filtered.reduce((sum, e) => {
                const trans = e.transactions as { amount: number; transaction_at: string } | null;
                return sum + (trans?.amount || 0);
            }, 0);
            const expenseCount = filtered.length;
            const avgExpense = expenseCount > 0 ? totalExpenses / expenseCount : 0;

            // Group by type
            const byType: Record<string, number> = {};
            filtered.forEach(e => {
                const trans = e.transactions as { amount: number; transaction_at: string } | null;
                const type = e.type || 'other';
                byType[type] = (byType[type] || 0) + (trans?.amount || 0);
            });

            const topCategory = Object.entries(byType).sort((a, b) => b[1] - a[1])[0];

            return {
                totalExpenses,
                expenseCount,
                avgExpense,
                byType: Object.entries(byType).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value),
                topCategory: topCategory ? { name: topCategory[0], value: topCategory[1] } : null,
            };
        },
    });

    const { data: monthlyData, isLoading: monthlyLoading } = useQuery({
        queryKey: ['expenses-analytics-monthly', fromDate, toDate],
        queryFn: async () => {
            const { data: expenses, error } = await supabase
                .from('expenses')
                .select('transactions(amount, transaction_at)');
            if (error) throw error;

            const months = eachMonthOfInterval({ start: monthRange.from, end: monthRange.to });
            const monthlyMap = new Map<string, number>();
            months.forEach(m => monthlyMap.set(format(m, 'yyyy-MM'), 0));

            (expenses || []).forEach(e => {
                const trans = e.transactions as { amount: number; transaction_at: string } | null;
                if (!trans?.transaction_at) return;
                const transDate = getDateFromTimestamp(trans.transaction_at);
                if (transDate < fromDate || transDate > toDate) return;
                const monthKey = transDate.substring(0, 7);
                if (monthlyMap.has(monthKey)) {
                    monthlyMap.set(monthKey, (monthlyMap.get(monthKey) || 0) + (trans.amount || 0));
                }
            });

            return Array.from(monthlyMap.entries()).map(([month, amount]) => ({
                month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' }),
                amount,
            }));
        },
    });

    if (summaryLoading) {
        return (
            <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
                </div>
                <Skeleton className="h-80" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-end">
                <MonthRangePicker monthRange={monthRange} onMonthRangeChange={setMonthRange} />
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Expenses</CardTitle>
                        <Receipt className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-red-600">{formatCurrency(summaryData?.totalExpenses || 0)}</div>
                        <p className="text-xs text-muted-foreground">{summaryData?.expenseCount || 0} transactions</p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Average Expense</CardTitle>
                        <TrendingDown className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{formatCurrency(summaryData?.avgExpense || 0)}</div>
                        <p className="text-xs text-muted-foreground">Per transaction</p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Top Category</CardTitle>
                        <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold capitalize">{summaryData?.topCategory?.name || 'N/A'}</div>
                        <p className="text-xs text-muted-foreground">{formatCurrency(summaryData?.topCategory?.value || 0)}</p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Categories</CardTitle>
                        <BarChart3 className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{summaryData?.byType.length || 0}</div>
                        <p className="text-xs text-muted-foreground">Expense types</p>
                    </CardContent>
                </Card>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                <Card className="border border-border/40">
                    <CardHeader>
                        <CardTitle>Monthly Expense Trend</CardTitle>
                        <CardDescription>Total expenses per month</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {monthlyLoading ? (
                            <Skeleton className="h-64" />
                        ) : (
                            <ResponsiveContainer width="100%" height={280}>
                                <AreaChart data={monthlyData}>
                                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                    <XAxis dataKey="month" className="text-xs" />
                                    <YAxis className="text-xs" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                                    <Tooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }} />
                                    <Area type="monotone" dataKey="amount" stroke="hsl(var(--destructive))" fill="hsl(var(--destructive))" fillOpacity={0.3} name="Expenses" />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader>
                        <CardTitle>Expenses by Category</CardTitle>
                        <CardDescription>Breakdown by expense type</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={280}>
                            <PieChart>
                                <Pie
                                    data={summaryData?.byType || []}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={5}
                                    dataKey="value"
                                    labelLine={false}
                                >
                                    {(summaryData?.byType || []).map((_, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Legend formatter={(value) => value.charAt(0).toUpperCase() + value.slice(1)} />
                                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                            </PieChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
